import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useInventoryService } from '../../../../apis/inventory/useInventoryService';
import {
  TInventoryCategory,
  TInventoryCategoryListApiResponse,
  TInventoryCategoryParams,
  TCreateInventoryCategoryRequest,
  TUpdateInventoryCategoryRequest,
} from '../../../../apis/inventory/inventory.api.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getInventoryCategoryListKey = 'get-inventory-category-list';
export const getInventoryCategoryByIdKey = 'get-inventory-category-by-id';

// ===================== LIST HOOK =====================
export const useInventoryCategoryListHook = (
  params: TInventoryCategoryParams,
  options?: UseQueryOptions<TInventoryCategoryListApiResponse, TQueryError>
): UseQueryResult<TInventoryCategoryListApiResponse, TQueryError> => {
  const { getInventoryCategoryList } = useInventoryService();

  return useQuery<TInventoryCategoryListApiResponse, TQueryError>({
    queryKey: [getInventoryCategoryListKey, params],
    queryFn: () => getInventoryCategoryList(params),
    enabled: true,
    ...options,
  });
};

// ===================== GET BY ID HOOK =====================
export const useInventoryCategoryByIdHook = (
  id: string,
  options?: UseQueryOptions<TInventoryCategory, TQueryError>
): UseQueryResult<TInventoryCategory, TQueryError> => {
  const { getInventoryCategoryById } = useInventoryService();

  return useQuery<TInventoryCategory, TQueryError>({
    queryKey: [getInventoryCategoryByIdKey, id],
    queryFn: () => getInventoryCategoryById(id),
    enabled: Boolean(id),
    ...options,
  });
};

// ===================== CREATE HOOK =====================
export const useCreateInventoryCategoryHook = (
  options?: UseMutationOptions<
    TInventoryCategory,
    TMutationError,
    TCreateInventoryCategoryRequest
  >
): UseMutationResult<
  TInventoryCategory,
  TMutationError,
  TCreateInventoryCategoryRequest
> => {
  const { createInventoryCategory } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<
    TInventoryCategory,
    TMutationError,
    TCreateInventoryCategoryRequest
  >({
    mutationFn: (category) => createInventoryCategory(category),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getInventoryCategoryListKey],
      });
    },
    ...options,
  });
};

// ===================== UPDATE HOOK =====================
export const useUpdateInventoryCategoryHook = (
  options?: UseMutationOptions<
    TInventoryCategory,
    TMutationError,
    { id: string; data: TUpdateInventoryCategoryRequest }
  >
): UseMutationResult<
  TInventoryCategory,
  TMutationError,
  { id: string; data: TUpdateInventoryCategoryRequest }
> => {
  const { updateInventoryCategory } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<
    TInventoryCategory,
    TMutationError,
    { id: string; data: TUpdateInventoryCategoryRequest }
  >({
    mutationFn: ({ id, data }) => updateInventoryCategory(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [getInventoryCategoryListKey],
      });
      queryClient.invalidateQueries({
        queryKey: [getInventoryCategoryByIdKey, variables.id],
      });
    },
    ...options,
  });
};

// ===================== DELETE HOOK =====================
export const useDeleteInventoryCategoryHook = (
  options?: UseMutationOptions<void, TMutationError, string>
): UseMutationResult<void, TMutationError, string> => {
  const { deleteInventoryCategory } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<void, TMutationError, string>({
    mutationFn: (id) => deleteInventoryCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getInventoryCategoryListKey],
      });
    },
    ...options,
  });
};
